import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../auth/AuthContext';
import { Sponsor, getSponsors, deleteContent } from '../lib/api';
import { SponsorEditorModal } from './SponsorEditorModal';
import './SponsorsSection.css';

export function SponsorsSection() {
  const { isAuthenticated, idToken } = useAuth();
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState<Sponsor | null>(null);

  const refresh = useCallback(() => {
    getSponsors()
      .then(setSponsors)
      .catch(() => {
        // Keep whatever list is already shown if the API isn't reachable/configured yet.
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleAdd = () => {
    setEditingSponsor(null);
    setEditorOpen(true);
  };

  const handleEdit = (sponsor: Sponsor) => {
    setEditingSponsor(sponsor);
    setEditorOpen(true);
  };

  const handleDelete = async (sponsor: Sponsor) => {
    if (!idToken) return;
    if (!window.confirm(`Remove ${sponsor.name}?`)) return;
    await deleteContent(sponsor['content-id'], idToken);
    refresh();
  };

  return (
    <section id="sponsors" className="sponsors">
      <div className="section-inner">
        <h2>Sponsors</h2>
        <p className="section-subcopy">We couldn't do this without our amazing sponsors.</p>

        {!loading && sponsors.length === 0 && (
          <p className="section-subcopy">No sponsors yet.</p>
        )}

        {sponsors.length > 0 && !isAuthenticated && (
          <div className="sponsors-carousel">
            <div
              className="sponsors-carousel-track"
              style={{ '--sponsor-count': sponsors.length } as React.CSSProperties}
            >
              {[...sponsors, ...sponsors].map((sponsor, i) => (
                <a
                  className="sponsor-carousel-item"
                  key={`${sponsor['content-id']}-${i}`}
                  href={sponsor.websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src={sponsor.imageUrl} alt={sponsor.name} />
                  <span>{sponsor.name}</span>
                </a>
              ))}
            </div>
          </div>
        )}

        {isAuthenticated && (
          <div className="sponsors-grid">
            {sponsors.map((sponsor) => (
              <div className="sponsor-card" key={sponsor['content-id']}>
                <a href={sponsor.websiteUrl} target="_blank" rel="noopener noreferrer">
                  <img src={sponsor.imageUrl} alt={sponsor.name} />
                  <span>{sponsor.name}</span>
                </a>
                <div className="sponsor-card-actions">
                  <button type="button" onClick={() => handleEdit(sponsor)} aria-label={`Edit ${sponsor.name}`}>
                    ✎
                  </button>
                  <button type="button" onClick={() => handleDelete(sponsor)} aria-label={`Remove ${sponsor.name}`}>
                    ✕
                  </button>
                </div>
              </div>
            ))}

            <button type="button" className="sponsor-card sponsor-card-add" onClick={handleAdd}>
              + Add Sponsor
            </button>
          </div>
        )}
      </div>

      <SponsorEditorModal
        open={editorOpen}
        sponsor={editingSponsor}
        onClose={() => setEditorOpen(false)}
        onSaved={refresh}
      />
    </section>
  );
}
